import { createContext, useContext, useEffect, useState } from 'react';

import { CampaignStoreItem } from '@givebacks/api/store';
import { atom, createStore, useAtom, useAtomValue } from 'jotai';
import { atomWithDefault } from 'jotai/vanilla/utils';

import { storeCauseIdAtom } from '../store/store.provider';

type SelectedCampaign = CampaignStoreItem;

const campaignCurrentCauseAtom = atomWithDefault<string | undefined>((get) =>
  get(storeCauseIdAtom)
);
campaignCurrentCauseAtom.debugLabel = 'Campaigns_CurrentCause';

const selectedCampaignAtom = atom<SelectedCampaign['uuid'] | undefined>(undefined);
selectedCampaignAtom.debugLabel = 'Campaigns_SelectedCampaign';

// The ideal scenario would be to have the following:
// This context is able to be used within the store context
const store = createStore();
const Context = createContext<typeof store | undefined>(undefined);

const CrowdfundingProvider = ({
  campaignId,
  children,
}: {
  campaignId?: string;
  children: React.ReactNode;
}) => {
  const [store] = useState(() => createStore());
  const storeCauseId = useAtomValue(storeCauseIdAtom);
  const [_campaignId, setCrowdfundingCampaign] = useAtom(selectedCampaignAtom, { store });
  const [causeId, setCrowdfundingCauseId] = useAtom(campaignCurrentCauseAtom, { store });

  useEffect(() => {
    if (campaignId && campaignId !== _campaignId) {
      setCrowdfundingCampaign(campaignId);
    }
  }, [_campaignId, campaignId, setCrowdfundingCampaign]);

  useEffect(() => {
    if (storeCauseId && storeCauseId !== causeId) {
      setCrowdfundingCauseId(storeCauseId);
    }
  }, [causeId, setCrowdfundingCauseId, storeCauseId]);

  return (
    <Context.Provider value={store}>
      {children}

      {/* <DevTools store={store} /> */}
    </Context.Provider>
  );
};

const useCrowdfundingProvider = () => {
  const store = useContext(Context);
  const [campaignId, setCrowdfundingCampaign] = useAtom(selectedCampaignAtom, { store: store });
  const [causeId, setCauseId] = useAtom(campaignCurrentCauseAtom, { store: store });
  return {
    setCrowdfundingCampaign,
    causeId,
    setCauseId,
    campaignId: campaignId ?? undefined,
  };
};

export { campaignCurrentCauseAtom, CrowdfundingProvider, useCrowdfundingProvider };
